/**
 * A speaker's face on the public site.
 *
 * The front-end counterpart to `PersonPortraitAvatar`, which serves the admin
 * tables. They are deliberately two components rather than one: a CRM row wants
 * a fixed pixel size and a status accent, while public surfaces size their faces
 * from the layout around them and are styled by page CSS. What they share is the
 * part that was being rewritten at every call site — the initials fallback and
 * the dead-URL guard — and both take those from `personInitials`.
 *
 * Renders a single element, an `<img>` or a `<span>`, never a wrapper. The events
 * page styles its faces through selectors like `.events-card__speaker img`, so
 * introducing a container would have silently broken the sizing and the
 * overlapping stack in the hero. `className` and `fallbackClassName` pass the
 * page's own classes straight through.
 *
 * The guard is the reason this exists. Every public site was rendering a raw
 * `<img>` with no `onError`, so a profile still pointing at a non-servable
 * storage prefix drew the browser's broken-image glyph in a row of otherwise
 * fine faces — which reads as a bug in the page rather than as one portrait
 * needing to be re-published.
 */
import { useEffect, useState } from "react";
import { Users } from "lucide-react";
import { personInitials } from "@/lib/personDisplay";
import ResponsiveImage from "@/components/ResponsiveImage";

/**
 * Speaker fields that name a group rather than a person.
 *
 * "Multiple Speakers" reduced to the initials "MS", which reads as somebody
 * called M. S. rather than as a panel. These get a group mark instead, so the
 * card says what it actually means.
 */
const GROUP_NAME = /\b(multiple|panel|speakers|various|team|guests|tbd|tba)\b/i;

export function isGroupSpeaker(name: string) {
  return GROUP_NAME.test(String(name ?? ""));
}

export default function SpeakerAvatar({
  name,
  portraitUrl,
  className,
  fallbackClassName,
  style,
  fallbackStyle,
  alt,
  decorative = true,
}: {
  name: string;
  portraitUrl?: string | null;
  /** The page's own class for the face, applied to whichever element renders. */
  className?: string;
  /** Added alongside `className` when the initials stand in. */
  fallbackClassName?: string;
  style?: React.CSSProperties;
  fallbackStyle?: React.CSSProperties;
  /**
   * Accessible name for the portrait, where it needs one.
   *
   * Supplying it makes the avatar non-decorative regardless of `decorative`: a
   * caller that has written an accessible name has, by doing so, said the image
   * carries meaning. The event card relies on this — its portrait is addressable
   * by name and there is a test that finds it that way.
   */
  alt?: string;
  /**
   * Decorative by default: these faces sit beside the speaker's name in text, so
   * announcing them again is noise. Pass false where the avatar is the only thing
   * naming the person.
   */
  decorative?: boolean;
}) {
  const [failed, setFailed] = useState(false);

  // A card can be re-used for a different speaker as a list filters or re-sorts,
  // so one person's dead portrait must not suppress the next person's good one.
  useEffect(() => {
    setFailed(false);
  }, [portraitUrl]);

  /*
   * `alt` names the photograph, not the stand-in.
   *
   * Two initials are not a picture of anyone, so the fallback stays decorative
   * even where the portrait is named -- the speaker's name is already beside it
   * in text, and announcing "AF speaker portrait" for a monogram is a small lie.
   * Only an explicit `decorative={false}` names the fallback.
   */
  const imageIsNamed = Boolean(alt) || !decorative;

  if (portraitUrl && !failed) {
    return (
      <ResponsiveImage
        className={className}
        src={portraitUrl}
        alt={imageIsNamed ? (alt ?? `${name} portrait`) : ""}
        aria-hidden={imageIsNamed ? undefined : true}
        onError={() => setFailed(true)}
        loading="lazy"
        decoding="async"
        maxWidth={192}
        sizes="96px"
        style={style}
      />
    );
  }

  return (
    <span
      className={[className, fallbackClassName].filter(Boolean).join(" ") || undefined}
      aria-hidden={decorative ? true : undefined}
      aria-label={decorative ? undefined : name}
      role={decorative ? undefined : "img"}
      style={{ ...style, ...fallbackStyle }}
    >
      {isGroupSpeaker(name)
        ? <Users size="65%" strokeWidth={1.8} aria-hidden="true" />
        : personInitials(name)}
    </span>
  );
}
